import { TabContext, TabPanel } from "@mui/lab"
import { Badge, Card, CardHeader, Divider, Grid, Tab, Tabs } from "@mui/material"
import { ListContextProvider, ReduxState, ShowBase } from "ra-core"
import { FC, useState } from "react"
import { useSelector } from "react-redux"
import { Content } from "~/components/content/Content"
import { EncryptedDatabasesContextProvider } from "~/components/databases/useEncryptedDatabases"
import useLocalListController from "~/components/list/useLocalListController"
import DatabaseMappingUpdatesTable from "~/components/obfuscation/DatabaseMappingUpdatesTable"
import DbESCList from "~/components/obfuscation/DbESCList"
import DbSubsettingList from "~/components/obfuscation/DbSubsettingList"
import EncryptionAlertsCard from "~/components/obfuscation/EncryptionAlertsCard"
import EncryptionStatusCard from "~/components/obfuscation/EncryptionStatusCard"
import { RecPatch } from "~/dataProvider/tools"
import { DatabaseResponseDto } from "~/gen/api/encrypt"
import { EncryptionMappingResponseDto } from "~/gen/api/governance"
import { OnDataFeature } from "~/gen/api/user"
import OnDataLock from "~/icons/OnDataLock"
import { getUserProfile } from "~/reducers/userProfile"
import { OdResourcePageProps } from "~/types/resource"
import { useResourceRegister } from "~/utils/resourceRegister"


const databaseListOptions : {sorters: Record<string, (a:EncryptionMappingResponseDto & RecPatch, b:EncryptionMappingResponseDto & RecPatch) => number>} = {
  sorters: {
    id: (a, b) => a.id < b.id ? -1 : 1,
    databaseName: (a, b) => (a.databaseName || '') < (b.databaseName || '') ? -1 : 1,
    sizeText: (a, b) => (a.size || 0) < (b.size || 0) ? -1 : 1,
    lastBackupTime: (a, b) => (a.lastBackupTime || '') < (b.lastBackupTime || '') ? -1 : 1,
  }
}

export const EncryptionDashboard: FC<OdResourcePageProps> = () => {
  useResourceRegister({ name: 'encryptable-backups' })
  const databaselistCtrl = useLocalListController({ resource: 'encryption/mappings', basePath: '' }, databaseListOptions)

  const [currentTab, setCurrentTab] = useState('mappings');
  const handleTabsChange = (event: any, newValue: string) => {
    setCurrentTab(newValue);
  };

  const hasDriverFeature = useSelector(store => Boolean(getUserProfile(store)?.features?.some(f => f.featureId === OnDataFeature.OnDataDriver)))

  return (
    <ShowBase resource="encryption/dashboard" basePath="" id="-1" >
      <Content pageName="Encryption Dashboard" resourceName="encryptionDashboard" >
        <Grid 
          container
          spacing={4}
          sx={{ mt: 2 }}
        >
          <Grid item xs={12} md={6}>
            <EncryptionStatusCard />
          </Grid>
          <Grid item xs={12} md={6}>
            <EncryptionAlertsCard />
          </Grid>
          <Grid item xs={12}>
            <TabContext value={currentTab}>
              <EncryptedDatabasesContextProvider>
                <TabCardWrapper {...({ currentTab, handleTabsChange, hasDriverFeature })}>
                  <ListContextProvider value={databaselistCtrl}>
                    <DatabaseMappingUpdatesTable />
                  </ListContextProvider>
                </TabCardWrapper>
              </EncryptedDatabasesContextProvider>
            </TabContext>

          </Grid>
        </Grid>
      </Content>
    </ShowBase>
  )
}

export default EncryptionDashboard;

const TabCardWrapper : FC<{handleTabsChange:(x:any, y:string)=>void, currentTab:string, hasDriverFeature:boolean}> = ({
  children,
  handleTabsChange,
  currentTab,
  hasDriverFeature,
}) => {
  const count = useSelector<ReduxState, number>(store => {
    const encryptedDbs = store.admin.resources['encrypted-dbs']?.data || {}
    return Object.values(encryptedDbs).filter(x => {
      const db = x as DatabaseResponseDto
      return db.encryptionKeyChanged || db.fieldsChanged
    }).length
  })
  return (
    <Card>
      <CardHeader 
        title={
          <Tabs
            indicatorColor="primary"
            onChange={handleTabsChange}
            scrollButtons="auto"
            textColor="primary"
            value={currentTab}
            variant="scrollable"
          >
            <Tab label="Encryption Mappings" value="mappings" />
            {hasDriverFeature && <Tab label={<Badge color="error" badgeContent={count} >Encryption Setting Change</Badge>} value="esc_list" />}
            <Tab label="Database Subsetting" value="subset_db_list" />
          </Tabs>
        }
      />
      <TabPanel value="mappings" sx={{ p: 0 }}>
        {children}
      </TabPanel>
      {hasDriverFeature &&
        <TabPanel value="esc_list" sx={{ p: 0 }}>
          <DbESCList />
        </TabPanel>}
      <TabPanel value="subset_db_list" sx={{ p: 0 }}>
        <DbSubsettingList />
      </TabPanel>
    </Card>
  )
}

const PlainCardWrapper : FC = ({children}) => {
  return (
    <Card>
      <CardHeader title="Encryption Mappings" />
      <Divider />
      {children}
    </Card>
  )
}
